import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useApi } from "../../hooks/useApi";
import { Brand } from "../../types/paymentProcessor";
import { BillingProfileSummary } from "../../types/billing";

interface BrandsManagerProps {
  brands: Brand[];
  onBrandsChange: () => void;
  createRequested?: boolean;
  onCreateHandled?: () => void;
  brandsApiUrl?: string;
  billingProfilesApiUrl?: string;
}

export default function BrandsManager({ brands, onBrandsChange, createRequested, onCreateHandled, brandsApiUrl = '/api/pp-accounts/brands', billingProfilesApiUrl = '/api/pp-accounts/billing-profiles' }: BrandsManagerProps) {
  const { post, put, del, loading } = useApi();
  const { get } = useApi();
  const [billingProfiles, setBillingProfiles] = useState<BillingProfileSummary[]>([]);
  const [billingProfileId, setBillingProfileId] = useState<number | "">("");
  const showBillingProfile = billingProfiles.length > 1;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [brandName, setBrandName] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState<Brand | null>(null);

  const [profileMenuAnchor, setProfileMenuAnchor] = useState<HTMLElement | null>(null);
  const [editingProfileBrandId, setEditingProfileBrandId] = useState<number | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await get<BillingProfileSummary[]>(billingProfilesApiUrl);
        if (!cancelled) setBillingProfiles(list);
      } catch {
        if (!cancelled) setBillingProfiles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [get, billingProfilesApiUrl]);

  const handleOpenCreate = useCallback(() => {
    setEditingBrand(null);
    setBrandName("");
    setBillingProfileId(billingProfiles.length === 1 ? billingProfiles[0].billingProfileId : "");
    setDialogOpen(true);
  }, [billingProfiles]);

  useEffect(() => {
    if (createRequested) {
      handleOpenCreate();
      onCreateHandled?.();
    }
  }, [createRequested, handleOpenCreate, onCreateHandled]);

  // --- Dialog handlers ---

  const handleOpenEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setBrandName(brand.brandName);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBrand(null);
    setBrandName("");
    setApiError(null);
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === "backdropClick") return;
    handleCloseDialog();
  };

  const handleOpenDelete = (brand: Brand) => {
    setDeletingBrand(brand);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingBrand(null);
    setApiError(null);
  };

  // --- CRUD operations ---

  const handleSubmit = async () => {
    if (!brandName.trim() || loading) return;

    try {
      if (editingBrand) {
        await put(`${brandsApiUrl}/${editingBrand.brandId}`, {
          name: brandName.trim(),
        });
        setSnackbar({ open: true, message: "Brand updated successfully", severity: "success" });
      } else {
        await post(brandsApiUrl, {
          name: brandName.trim(),
          ...(billingProfileId === "" ? {} : { billingProfileId }),
        });
        setSnackbar({ open: true, message: "Brand created successfully", severity: "success" });
      }
      handleCloseDialog();
      onBrandsChange();
    } catch (err: any) {
      setApiError(err.message || "An error occurred");
    }
  };

  const closeProfileMenu = () => {
    setProfileMenuAnchor(null);
    setEditingProfileBrandId(null);
  };

  const handleChangeBillingProfile = async (brand: Brand, billingProfileId: number) => {
    closeProfileMenu();
    if (billingProfileId === brand.billingProfileId) return;

    try {
      await put(`${brandsApiUrl}/${brand.brandId}`, {
        name: brand.brandName,
        billingProfileId,
      });
      setSnackbar({ open: true, message: "Billing profile updated", severity: "success" });
      onBrandsChange();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || "Failed to update billing profile", severity: "error" });
    }
  };

  const handleDelete = async () => {
    if (!deletingBrand || loading) return;

    try {
      await del(`${brandsApiUrl}/${deletingBrand.brandId}`);
      setSnackbar({ open: true, message: "Brand deleted successfully", severity: "success" });
      handleCloseDelete();
      onBrandsChange();
    } catch (err: any) {
      setApiError(err.message || "An error occurred");
    }
  };

  return (
    <div>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Brand Name</TableCell>
                <TableCell>Billing profile</TableCell>
                <TableCell sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brands.map((brand) => (
                <TableRow key={brand.brandId} hover>
                  <TableCell>{brand.brandName}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        "& .billing-profile-edit-icon": { opacity: 0 },
                        "&:hover .billing-profile-edit-icon": { opacity: 1 },
                      }}
                      onClick={(e) => {
                        setProfileMenuAnchor(e.currentTarget);
                        setEditingProfileBrandId(brand.brandId);
                      }}
                    >
                      <Typography variant="body2">
                        {billingProfiles.find((p) => p.billingProfileId === brand.billingProfileId)?.name ?? "—"}
                      </Typography>
                      <EditIcon className="billing-profile-edit-icon" sx={{ fontSize: 14, color: "text.secondary" }} />
                    </Box>
                    <Menu
                      anchorEl={editingProfileBrandId === brand.brandId ? profileMenuAnchor : null}
                      open={editingProfileBrandId === brand.brandId && !!profileMenuAnchor}
                      onClose={closeProfileMenu}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                      {billingProfiles.map((p) => (
                        <MenuItem
                          key={p.billingProfileId}
                          selected={p.billingProfileId === brand.billingProfileId}
                          onClick={() => handleChangeBillingProfile(brand, p.billingProfileId)}
                        >
                          {p.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" onClick={() => handleOpenEdit(brand)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDelete(brand)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {brands.length === 0 && (
        <Box className="text-center py-8">
          <Typography variant="body1" color="text.secondary">
            No brands found.
          </Typography>
        </Box>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBrand ? "Edit Brand" : "Create Brand"}</DialogTitle>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Brand Name"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              fullWidth
              required
              disabled={loading}
              size="small"
              autoFocus
            />
            {showBillingProfile && !editingBrand && (
              <FormControl fullWidth size="small" required>
                <InputLabel id="brand-billing-profile-label">Billed through</InputLabel>
                <Select
                  labelId="brand-billing-profile-label"
                  label="Billed through"
                  value={billingProfileId}
                  onChange={(e) => setBillingProfileId(Number(e.target.value))}
                  disabled={loading}
                >
                  {billingProfiles.map((p) => (
                    <MenuItem key={p.billingProfileId} value={p.billingProfileId}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !brandName.trim() || (showBillingProfile && !editingBrand && billingProfileId === "")}
            sx={{
              backgroundColor: "#465fff",
              "&:hover": { backgroundColor: "#3641f5" },
            }}
          >
            {loading ? "Saving..." : editingBrand ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete}>
        <DialogTitle>Delete Brand</DialogTitle>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete <strong>{deletingBrand?.brandName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDelete} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
